import { i18n } from 'i18n';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import {
  BulkInviteForm,
  BulkInviteResultDto,
  ProjectAccessDto,
  ProjectAccountDto,
  SetProjectAccountCommand
} from './accountAdminApi';
import useApi, { makeGetQuery, makeUpdateMutation } from './useApi';

export * from './accountAdminApi';

export const PROJECT_ACCOUNT_KEY = 'project-account';
export const PROJECT_ACCESS_KEY = 'project-access';

export const useProjectAccountQuery = makeGetQuery<ProjectAccountDto, string>(
  PROJECT_ACCOUNT_KEY,
  (api, projectId) => () => api.accountAdmin.getProjectAccount(projectId),
);

export const useSetProjectAccountMutation = makeUpdateMutation<void, SetProjectAccountCommand>(
  PROJECT_ACCOUNT_KEY,
  null,
  (api, projectId) => (command) => api.accountAdmin.setProjectAccount(projectId, command),
  () => i18n._('Account details updated'),
);

export const useProjectAccessQuery = makeGetQuery<ProjectAccessDto, string>(
  PROJECT_ACCESS_KEY,
  (api, projectId) => () => api.accountAdmin.getProjectAccess(projectId),
);

// No success snackbar here: a bulk invite has one outcome per address, so callers show the result list.
export const useBulkInviteMutation = (options?: UseMutationOptions<BulkInviteResultDto[], unknown, BulkInviteForm>) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<BulkInviteResultDto[], unknown, BulkInviteForm>(
    (form) => api.accountAdmin.bulkInvite(form),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries('invitations', { exact: false });
        queryClient.invalidateQueries(PROJECT_ACCESS_KEY, { exact: false });
        if (options?.onSuccess) {
          options.onSuccess(...args);
        }
      },
    },
  );
};

/** Splits a pasted list of addresses on commas, semicolons, spaces or new lines; drops blanks and repeats. */
export const parseEmailList = (value: string): string[] => {
  const seen = new Set<string>();

  return value
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email) => {
      const key = email.toLowerCase();
      if (!email || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

export const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
